/**
 * BM Versioning MVI State
 *
 * Architecture: MVI Pattern - State Layer
 */

import type { TFeatureId } from "@/shared/types/feature";
import type { IFeatureCommitInfo, IFeatureHistory } from "@/shared/types/bm-versioning";
import type { IApiError } from "@/shared/types";

// ============================================================================
// Domain State
// ============================================================================

export interface IVersioningDomainState {
  /** Current feature ID being versioned */
  readonly currentFeatureId: TFeatureId | null;

  /** Commit history for the current feature */
  readonly history: IFeatureHistory | null;

  /** Whether there are unsaved changes (dirty state) */
  readonly isDirty: boolean;
}

// ============================================================================
// UI State
// ============================================================================

export interface IVersioningUIState {
  /** Loading states */
  readonly isLoadingHistory: boolean;
  readonly isCommitting: boolean;
  readonly isCheckingOut: boolean;

  /** Error state */
  readonly error: IApiError | null;

  /** History panel visibility */
  readonly isHistoryPanelOpen: boolean;

  /** Commit dialog visibility */
  readonly isCommitDialogOpen: boolean;

  /** Feature ID being checked out (for confirmation) */
  readonly checkoutTargetId: TFeatureId | null;
}

// ============================================================================
// Combined State
// ============================================================================

export interface IBmVersioningState {
  readonly domain: IVersioningDomainState;
  readonly ui: IVersioningUIState;
}

// ============================================================================
// Initial State Factory
// ============================================================================

export function createInitialDomainState(): IVersioningDomainState {
  return {
    currentFeatureId: null,
    history: null,
    isDirty: false,
  };
}

export function createInitialUIState(): IVersioningUIState {
  return {
    isLoadingHistory: false,
    isCommitting: false,
    isCheckingOut: false,
    error: null,
    isHistoryPanelOpen: false,
    isCommitDialogOpen: false,
    checkoutTargetId: null,
  };
}

export function createInitialState(): IBmVersioningState {
  return {
    domain: createInitialDomainState(),
    ui: createInitialUIState(),
  };
}

// ============================================================================
// Selectors
// ============================================================================

/**
 * Check if commit is available (has dirty changes and not loading)
 */
export function selectCanCommit(state: IBmVersioningState): boolean {
  return (
    state.domain.isDirty &&
    !state.ui.isCommitting &&
    !state.ui.isCheckingOut &&
    state.domain.currentFeatureId !== null
  );
}

/**
 * Check if checkout is available (not in loading state)
 */
export function selectCanCheckout(state: IBmVersioningState): boolean {
  return !state.ui.isCommitting && !state.ui.isCheckingOut;
}

/**
 * Check if any operation is in progress
 */
export function selectIsLoading(state: IBmVersioningState): boolean {
  return state.ui.isLoadingHistory || state.ui.isCommitting || state.ui.isCheckingOut;
}

/**
 * Get the current HEAD commit (if history is loaded)
 */
export function selectCurrentHead(state: IBmVersioningState): IFeatureCommitInfo | null {
  if (!state.domain.history) return null;
  return state.domain.history.commits.find((c) => c.isHead) ?? null;
}

/**
 * Get archived commits (non-HEAD commits)
 */
export function selectArchivedCommits(
  state: IBmVersioningState
): readonly IFeatureCommitInfo[] {
  if (!state.domain.history) return [];
  return state.domain.history.commits.filter((c) => !c.isHead);
}

/**
 * Get commit count
 */
export function selectCommitCount(state: IBmVersioningState): number {
  return state.domain.history?.commits.length ?? 0;
}
